import * as THREE from 'three';
import { SceneConfig, DEFAULT_SCENE_CONFIG } from './SceneConfig';
import { CameraController } from '../camera/CameraController';
import { LightingSystem } from './LightingSystem';
import { RenderLoop, RenderStats } from './RenderLoop';
import { InteractionManager } from '../../interaction/InteractionManager';
import { useGalaxyStore } from '../../core/state/GalaxyStore';

import { VisualElementsManager } from '../elements/VisualElementsManager';
import { EffectsManager } from '../effects/EffectsManager';
import { PostProcessingPipeline } from '../effects/PostProcessingPipeline';

export interface SceneState {
  metadata: {
    timestamp: number;
    frameCount: number;
    objectCount: number;
  };
  
  objects: SerializedObject[];
  lights: SerializedLight[];
  camera: {
    position: [number, number, number];
    rotation: [number, number, number];
    fov: number;
  };
  
  performance: {
    fps: number;
    renderTime: number;
    memoryUsage: number;
  };
}

export interface SerializedObject {
  id: string;
  type: string;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  visible: boolean;
  userData: any;
}

export interface SerializedLight {
  id: string;
  type: string;
  intensity: number;
  color: number;
  position: [number, number, number];
}

export interface SceneManager {
  scene: THREE.Scene;
  renderer: THREE.WebGLRenderer;
  camera: CameraController;
  
  // Lifecycle
  initialize(container: HTMLElement): void;
  update(deltaTime: number): void;
  dispose(): void;
  
  // Object management
  addObject(id: string, object: THREE.Object3D): void;
  removeObject(id: string): boolean;
  getObjectById(id: string): THREE.Object3D | null;
  
  // State export for testing
  exportState(): SceneState;
  
  // Effects management
  getEffectsManager(): any;
}

export class GalaxyScene implements SceneManager {
  public scene: THREE.Scene;
  public renderer: THREE.WebGLRenderer;
  public camera: CameraController;
  
  private _config: SceneConfig;
  private lighting: LightingSystem;
  private renderLoop: RenderLoop;
  private container: HTMLElement | null = null;
  
  // Object management
  private objectRegistry: Map<string, THREE.Object3D> = new Map();
  private frameCount: number = 0;
  
  // Visual elements manager
  private visualElements: VisualElementsManager;
  public effectsManager: EffectsManager;
  public postProcessing: PostProcessingPipeline;
  
  // Interaction system
  private interactionManager: InteractionManager | null = null;
  
  // Performance monitoring
  private currentStats: RenderStats = {
    fps: 0,
    frameTime: 0,
    drawCalls: 0,
    triangles: 0,
    geometries: 0,
    textures: 0,
    memoryUsage: 0,
  };
  
  constructor(config?: Partial<SceneConfig>) {
    this._config = { ...DEFAULT_SCENE_CONFIG, ...config };
    
    // Initialize Three.js core
    this.scene = new THREE.Scene();
    this.renderer = this.createRenderer();
    this.setupScene();
    
    // Initialize subsystems
    this.camera = new CameraController(this.renderer);
    this.lighting = new LightingSystem(this.scene);
    this.renderLoop = new RenderLoop(this.renderer, this.scene, this.camera.camera);
    
    // Initialize effects management
    this.effectsManager = new EffectsManager(
      this.scene,
      this.renderer,
      this.camera.camera,
      {
        targetFPS: 60,
        effectsBudget: 8,
        autoQualityAdjustment: true,
        reducedMotion: false
      }
    );
    
    // Initialize post-processing pipeline
    this.postProcessing = new PostProcessingPipeline(
      this.renderer,
      this.scene,
      this.camera.camera
    );
    
    // Initialize visual elements manager
    this.visualElements = new VisualElementsManager({
      sunRadius: 10,
      sphereRadius: 20,
      enableFleets: true,
      enableFlares: true,
      qualityLevel: 'high',
    });
    
    // Register effects with the effects manager
    this.registerEffectsWithManager();
    
    // Add visual elements to scene
    this.addObject('visual-elements', this.visualElements);
    
    this.setupRenderLoop();
  }
  
  private registerEffectsWithManager(): void {
    // Register all effects from visual elements manager
    const solarFlares = this.visualElements.getSolarFlares();
    const mempoolNebula = this.visualElements.getMempoolNebula();
    
    if (solarFlares) {
      this.effectsManager.registerEffect('solarFlares', solarFlares);
    }
    
    if (mempoolNebula) {
      this.effectsManager.registerEffect('mempoolNebula', mempoolNebula);
    }
    
    // Register other effects as they become available
    // Note: Some effects might be created later, so we'll need to register them dynamically
  }
  
  private createRenderer(): THREE.WebGLRenderer {
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    
    // Enable shadows
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    
    // Set color space and tone mapping
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.5;
    
    // Background
    renderer.setClearColor(0x000511, 1.0); // Deep space blue
    
    return renderer;
  }
  
  private setupScene(): void {
    // Add fog for depth perception
    this.scene.fog = new THREE.Fog(0x000511, 500, 2000);
    
    // Add starfield background
    this.createStarfield();
  }
  
  private createStarfield(): void {
    const starGeometry = new THREE.BufferGeometry();
    const starCount = 5000;
    const positions = new Float32Array(starCount * 3);
    
    for (let i = 0; i < starCount * 3; i += 3) {
      // Create random positions in a sphere
      const radius = 1000 + Math.random() * 500;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      
      positions[i] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i + 2] = radius * Math.cos(phi);
    }
    
    starGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const starMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 1,
      sizeAttenuation: false,
    });
    
    const stars = new THREE.Points(starGeometry, starMaterial);
    stars.userData = { id: 'starfield', type: 'background' };
    this.scene.add(stars);
    this.objectRegistry.set('starfield', stars);
  }
  
  private setupRenderLoop(): void {
    this.renderLoop.setOnFrame((deltaTime: number) => {
      this.update(deltaTime);
      
      // Use post-processing pipeline for rendering
      this.postProcessing.render(deltaTime);
    });
    
    this.renderLoop.setOnStatsUpdate((stats: RenderStats) => {
      this.currentStats = stats;
    });
  }
  
  private setupInteractionSystem(): void {
    const canvas = this.renderer.domElement;
    const store = useGalaxyStore;
    
    this.interactionManager = new InteractionManager(
      canvas,
      this.camera.camera,
      this.scene,
      store,
      this.camera,
      {
        enableMouse: true,
        enableKeyboard: true,
        enableTouch: true,
        raycastThrottle: 33,
        maxInteractiveObjects: 1000,
      }
    );
    
    // Connect interaction events to effects manager
    this.setupInteractionEffects();
    
    console.log('Interaction system initialized with effects integration');
  }

  
  private setupInteractionEffects(): void {
    if (!this.interactionManager) return;
    
    // TODO: Connect interaction events to effects manager once event system is available
    // For now, effects can be triggered manually through the public handleInteraction method
    console.log('[GalaxyScene] Interaction effects system initialized (manual mode)');
  }
  
  initialize(container: HTMLElement): void {
    this.container = container;
    container.appendChild(this.renderer.domElement);
    
    // Setup interaction system
    this.setupInteractionSystem();
    
    // Setup resize handler
    this.setupResizeHandler();
    
    // Start render loop
    this.renderLoop.start();
  }
  
  private setupResizeHandler(): void {
    const handleResize = () => {
      if (!this.container) return;
      
      const width = this.container.clientWidth;
      const height = this.container.clientHeight;
      
      this.camera.onResize(width, height);
      this.renderer.setSize(width, height);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial size
  }
  
  update(deltaTime: number): void {
    this.frameCount++;
    
    // Update subsystems
    this.camera.update(deltaTime);
    this.lighting.update(deltaTime);
    
    // Update effects manager (this handles all visual effects)
    this.effectsManager.update(deltaTime);
    
    // Update interaction system
    if (this.interactionManager) {
      this.interactionManager.update(deltaTime);
    }
    
    // Update any animated objects
    this.updateAnimatedObjects(deltaTime);
  }
  
  private updateAnimatedObjects(deltaTime: number): void {
    // Example: rotate starfield slowly
    const starfield = this.objectRegistry.get('starfield');
    if (starfield) {
      starfield.rotation.y += deltaTime * 0.01;
    }
    
    // Update visual elements (Bitcoin Sun, Dyson Sphere, etc.)
    this.visualElements.update(deltaTime);
  }
  
  addObject(id: string, object: THREE.Object3D): void {
    // Remove existing object with same ID
    this.removeObject(id);
    
    // Add to scene and registry
    object.userData = { ...object.userData, id };
    this.scene.add(object);
    this.objectRegistry.set(id, object);
  }
  
  removeObject(id: string): boolean {
    const object = this.objectRegistry.get(id);
    if (object) {
      this.scene.remove(object);
      this.objectRegistry.delete(id);
      return true;
    }
    return false;
  }
  
  getObjectById(id: string): THREE.Object3D | null {
    return this.objectRegistry.get(id) || null;
  }
  
  getObjectsByType(type: string): THREE.Object3D[] {
    return Array.from(this.objectRegistry.values()).filter(
      obj => obj.userData['type'] === type
    );
  }
  
  exportState(): SceneState {
    const objects: SerializedObject[] = Array.from(this.objectRegistry.entries()).map(
      ([id, obj]) => ({
        id,
        type: obj.userData['type'] || 'unknown',
        position: obj.position.toArray() as [number, number, number],
        rotation: obj.rotation.toArray().slice(0, 3) as [number, number, number],
        scale: obj.scale.toArray() as [number, number, number],
        visible: obj.visible,
        userData: obj.userData,
      })
    );
    
    const lights: SerializedLight[] = [];
    this.scene.traverse((child) => {
      if (child instanceof THREE.Light) {
        lights.push({
          id: child.userData['id'] || `light_${lights.length}`,
          type: child.type,
          intensity: child.intensity,
          color: child.color.getHex(),
          position: child.position.toArray() as [number, number, number],
        });
      }
    });
    
    return {
      metadata: {
        timestamp: Date.now(),
        frameCount: this.frameCount,
        objectCount: this.objectRegistry.size,
      },
      objects,
      lights,
      camera: {
        position: this.camera.camera.position.toArray() as [number, number, number],
        rotation: this.camera.camera.rotation.toArray().slice(0, 3) as [number, number, number],
        fov: this.camera.camera.fov,
      },
      performance: {
        fps: this.currentStats.fps,
        renderTime: this.currentStats.frameTime,
        memoryUsage: this.currentStats.memoryUsage,
      },
    };
  }
  
  setQuality(level: 'low' | 'medium' | 'high' | 'ultra'): void {
    this.renderLoop.setQuality(level);
    this.visualElements.setQualityLevel(level);
    
    // Update effects manager quality
    this.effectsManager.setQuality(level);
    
    // Update post-processing quality
    this.postProcessing.setQuality(level);
  }
  
  
  // Visual elements access methods
  public getBitcoinSun() {
    return this.visualElements.getBitcoinSun();
  }
  
  public getDysonSphere() {
    return this.visualElements.getDysonSphere();
  }
  
  public getSolarFlares() {
    return this.visualElements.getSolarFlares();
  }
  
  public getVisualElementsManager() {
    return this.visualElements;
  }

  
  // Effects management methods
  public getEffectsManager() {
    return this.effectsManager;
  }
  
  public getPostProcessing() {
    return this.postProcessing;
  }
  
  public handleInteraction(event: { type: 'hover' | 'click' | 'select' | 'camera_transition'; object?: THREE.Object3D; position?: THREE.Vector3; intensity?: number }): void {
    this.effectsManager.handleInteraction(event);
  }
  
  public getEffectsPerformanceStats() {
    return this.effectsManager.getPerformanceStats();
  }

  dispose(): void {
    // Stop render loop
    this.renderLoop.dispose();
    
    // Clean up interaction system
    if (this.interactionManager) {
      this.interactionManager.dispose();
      this.interactionManager = null;
    }
    
    // Clean up effects manager
    this.effectsManager.dispose();
    
    // Clean up post-processing pipeline
    this.postProcessing.dispose();
    
    // Clean up visual elements
    this.visualElements.dispose();
    
    // Clean up subsystems
    this.camera.dispose();
    this.lighting.dispose();
    
    // Clean up objects
    for (const object of this.objectRegistry.values()) {
      if ('dispose' in object && typeof object.dispose === 'function') {
        object.dispose();
      }
    }
    this.objectRegistry.clear();
    
    // Clean up renderer
    this.renderer.dispose();
    
    // Remove from DOM
    if (this.container && this.renderer.domElement.parentNode) {
      this.container.removeChild(this.renderer.domElement);
    }
  }
}